import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { getVehicles } from '@/config/vehicles.config';
import { Vehicle } from '@/types/vehicle.types';
import { SchemaMarkup } from '@/components/common/SchemaMarkup';
import { CanonicalTag } from '@/components/common/CanonicalTag';

const TYPE_ORDER = [
    'Full-size pickup truck',
    'Midsize pickup truck',
    'Compact SUV',
    'Compact car',
];

const normalizeType = (vehicleType: string): string => {
    const lower = vehicleType.toLowerCase();
    if (lower.includes('compact') && (lower.includes('car') || lower.includes('sedan'))) return 'Compact car';
    for (const type of TYPE_ORDER) {
        if (vehicleType === type) return type;
    }
    return vehicleType;
};

export const VehiclesIndexPage: React.FC = () => {
    const navigate = useNavigate();
    const vehicles = getVehicles();

    const itemListSchema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Vehicles Available at DriveSource Dealerships',
        itemListElement: vehicles.map((vehicle, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: `${vehicle.make} ${vehicle.model}`,
            url: `https://getdrivesource.com${vehicle.path}`,
        })),
    };

    // Group vehicles by normalized type
    const grouped = vehicles.reduce<Record<string, Vehicle[]>>((acc, vehicle) => {
        const type = normalizeType(vehicle.vehicleType);
        if (!acc[type]) acc[type] = [];
        acc[type].push(vehicle);
        return acc;
    }, {});

    // Sort types by predefined order, then alphabetically for any extras
    const sortedTypes = [
        ...TYPE_ORDER.filter(t => grouped[t]),
        ...Object.keys(grouped).filter(t => !TYPE_ORDER.includes(t)).sort(),
    ];

    return (
        <>
            <CanonicalTag href="https://getdrivesource.com/vehicles" />
            <SchemaMarkup schema={itemListSchema} />
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-48 sm:pb-36 px-4 sm:px-6">
                <div className="max-w-3xl mx-auto">
                    {/* Hero */}
                    <div className="text-center mb-10 sm:mb-14">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">
                            Browse Vehicles
                        </h1>
                        <p className="text-gray-300 text-sm sm:text-base max-w-xl mx-auto">
                            Research trucks, SUVs, and cars available from dealerships in southern Saskatchewan.
                        </p>
                    </div>

                    {/* Vehicle type sections */}
                    <div className="space-y-10">
                        {sortedTypes.map(type => (
                            <section key={type}>
                                <div className="mb-4 pb-2 border-b border-gray-700">
                                    <h2 className="text-lg sm:text-xl font-bold text-white">{type}</h2>
                                </div>
                                <ul className="space-y-2">
                                    {grouped[type].map(vehicle => (
                                        <li key={vehicle.slug}>
                                            <button
                                                onClick={() => navigate(vehicle.path)}
                                                className="w-full flex items-center justify-between gap-3 px-4 py-4 rounded-xl bg-[#1a1a1a] hover:bg-cyan-400/10 border border-gray-700 hover:border-cyan-400/40 transition-all text-left group"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <span className="block text-sm sm:text-base text-gray-200 group-hover:text-white transition-colors font-semibold">
                                                        {vehicle.make} {vehicle.model}
                                                    </span>
                                                    <span className="block text-xs sm:text-sm text-gray-400 mt-0.5">
                                                        {vehicle.vehicleType}
                                                    </span>
                                                </div>
                                                <ChevronRight className="size-4 text-gray-500 group-hover:text-cyan-400 flex-shrink-0 transition-colors" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))}
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};
